import { Box, CircularProgress, SpeedDialActionProps, Tooltip } from "@mui/material";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { SpeedDialAction } from "components";
import IconButtonExt from "components/iconButtonExt";
import { TooltipHotkey } from "components/tooltipHotkey";
import { featuresConfig } from "config/features";

import { useFlyToSelected } from "./useFlyToSelected";

type Props = SpeedDialActionProps & {
    position?: { top?: number; right?: number; bottom?: number; left?: number };
};

export function FlyToSelected({ position, newDesign, ...speedDialProps }: Props & { newDesign?: boolean }) {
    const { nameKey, Icon } = featuresConfig.flyToSelected;
    const { t } = useTranslation();

    const [tooltipMessage, setTooltipMessage] = useState("");
    const tooltipTimeout = useRef<ReturnType<typeof setTimeout>>();

    const { flyToSelected, orthoLoading, isLoading, disabled } = useFlyToSelected();

    const showTooltip = (msg: string) => {
        if (tooltipTimeout.current) {
            clearTimeout(tooltipTimeout.current);
        }
        setTooltipMessage(msg);
        tooltipTimeout.current = setTimeout(() => {
            setTooltipMessage("");
        }, 3000);
    };

    const handleClick = () => {
        flyToSelected({ showTooltip });
    };

    if (newDesign) {
        return (
            <Tooltip open={Boolean(tooltipMessage)} title={tooltipMessage} placement="top">
                <Box>
                    <Tooltip
                        title={
                            <Box display="flex" alignItems="center" gap={1}>
                                {t(nameKey)}
                                <TooltipHotkey>Space</TooltipHotkey>
                            </Box>
                        }
                        placement="top"
                    >
                        <Box>
                            <IconButtonExt onClick={handleClick} disabled={disabled} loading={isLoading}>
                                <Icon />
                            </IconButtonExt>
                        </Box>
                    </Tooltip>
                </Box>
            </Tooltip>
        );
    }

    return (
        <SpeedDialAction
            {...speedDialProps}
            data-test="flyToSelected"
            FabProps={{
                ...speedDialProps.FabProps,
                disabled,
                style: { ...position, position: "absolute" },
            }}
            onClick={handleClick}
            title={disabled ? undefined : t(nameKey)}
            icon={
                <Tooltip open={Boolean(tooltipMessage)} title={tooltipMessage} placement="top">
                    <Box
                        width={1}
                        height={1}
                        position="relative"
                        display="inline-flex"
                        justifyContent="center"
                        alignItems="center"
                    >
                        {isLoading || orthoLoading ? (
                            <CircularProgress thickness={2.5} sx={{ position: "absolute" }} />
                        ) : null}
                        <Icon />
                    </Box>
                </Tooltip>
            }
        />
    );
}
